/**
* NAATI JSON-LD builders — shared across every /naati/* route so
* FAQPage, LegalService, Service, Offer, and BreadcrumbList are
* emitted in the SAME shape site-wide (schema validators love that).
*/
import type { NaatiCity, NaatiDocType } from "@/data/naati";
import type { NaatiPageContent } from "./generator";
import { SITE } from "@/config/sites";

export function buildNaatiGraph(args: {
  url: string;
  title: string;
  description: string;
  content: NaatiPageContent;
  service?: NaatiDocType;
  city?: NaatiCity;
  breadcrumb: Array<{ name: string; url: string }>;
}) {
  const org = {
    "@type": "LegalService",
    "@id": `${SITE.canonicalHost ? `https://${SITE.canonicalHost}` : ""}/#org`,
    name: SITE.name,
    url: SITE.canonicalHost ? `https://${SITE.canonicalHost}` : undefined,
    areaServed: ["TH", "AU", "NZ", "UK", "US", "CA", "SG", "AE", "DE", "JP", "KR"],
  };

  const webPage = {
    "@type": "WebPage",
    "@id": args.url,
    url: args.url,
    name: args.title,
    description: args.description,
    inLanguage: "th-TH",
    isPartOf: { "@id": org["@id"] },
    speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", "h3", ".speakable-summary", ".lead", ".answer-summary", ".faq-answer", "[data-speakable]"] },
  };

  const faq = {
    "@type": "FAQPage",
    mainEntity: args.content.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const service = args.service
    ? {
        "@type": "Service",
        name: `NAATI Certified Translation — ${args.service.nameEn}`,
        serviceType: "Certified Translation",
        provider: { "@id": org["@id"] },
        areaServed: args.city ? args.city.country : ["AU", "NZ", "UK", "US", "CA"],
        offers: { "@type": "Offer", availability: "https://schema.org/InStock", url: args.url },
      }
    : null;

  const howTo = {
    "@type": "HowTo",
    name: `วิธีสั่งงานแปล NAATI (${args.service?.nameTh ?? "ทุกประเภทเอกสาร"})`,
    step: args.content.steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.tag,
      text: s.text,
    })),
  };

  const breadcrumb = {
    "@type": "BreadcrumbList",
    itemListElement: args.breadcrumb.map((b, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: b.name,
      item: b.url,
    })),
  };

  return {
    "@context": "https://schema.org",
    "@graph": [org, webPage, faq, ...(service ? [service] : []), howTo, breadcrumb].filter(Boolean),
  };
}
